import type { OpenMetadataHit } from '@/shared/api/openmetadata/types';

import type { DataZone, DataZoneLowerCaseString } from '@/entities/quality';

import { ALL_ICEBERG_DATA } from './icebergData';

type QualityDashboardFamily = 'gsp' | 'hanwha' | 'kmp' | 'gsc';
type QualityDashboardRole =
  | 'chart'
  | 'trend'
  | 'bearings'
  | 'dcs'
  | 'waveform_preprocessed'
  | 'config'
  | 'feature_preprocessed'
  | 'diagnosis_result';

type QualityRatio = {
  good: number;
  poor: number;
  unknown: number;
};

type OpenMetadataSourceWithLineage = OpenMetadataHit['_source'] & {
  upstreamLineage?: Array<unknown>;
};

export type QualityDashboardIcebergTable = {
  tableId: number;
  sourceId: string;
  fqn: string;
  tableName: string;
  dataZone: DataZoneLowerCaseString;
  dataZoneLabel: DataZone;
  service: string;
  database: string;
  schema: string;
  namespace: string;
  columnNames: string[];
  columnCount: number;
  description: string;
  upstreamLineageCount: number;
  updatedAt: number;
  family: QualityDashboardFamily;
  role: QualityDashboardRole;
  roleQualityRatio: QualityRatio;
  latestTotalRows: number;
};

const DATA_ZONE_LABEL_MAP: Record<DataZoneLowerCaseString, DataZone> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
};

export const QUALITY_DASHBOARD_FAMILY_MULTIPLIER_MAP: Record<QualityDashboardFamily, number> = {
  gsp: 1.25,
  hanwha: 1,
  kmp: 0.9,
  gsc: 0.35,
};

export const QUALITY_DASHBOARD_ROLE_QUALITY_RATIO_MAP: Record<QualityDashboardRole, QualityRatio> = {
  chart: { good: 86, poor: 10, unknown: 4 },
  trend: { good: 90, poor: 7, unknown: 3 },
  bearings: { good: 97, poor: 2, unknown: 1 },
  dcs: { good: 88, poor: 8, unknown: 4 },
  waveform_preprocessed: { good: 94, poor: 4, unknown: 2 },
  config: { good: 98, poor: 1, unknown: 1 },
  feature_preprocessed: { good: 97, poor: 2, unknown: 1 },
  diagnosis_result: { good: 99, poor: 1, unknown: 0 },
};

const stripHtml = (value?: string): string => {
  if (!value) return '';

  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
};

const getDataZone = (serviceName: string): DataZoneLowerCaseString => {
  if (serviceName.startsWith('gold')) return 'gold';
  if (serviceName.startsWith('silver')) return 'silver';
  return 'bronze';
};

const getFamily = (tableName: string): QualityDashboardFamily => {
  if (tableName.startsWith('gsp_ecms1_') || tableName === 'gsp_dcs') return 'gsp';
  if (tableName.startsWith('kmp_ecms_')) return 'kmp';
  if (tableName.startsWith('hanwha_impact_')) return 'hanwha';
  return 'gsc';
};

const getRole = (tableName: string, dataZone: DataZoneLowerCaseString): QualityDashboardRole => {
  if (dataZone === 'bronze') {
    if (tableName.endsWith('_chart')) return 'chart';
    if (tableName.endsWith('_trend')) return 'trend';
    if (tableName === 'gsp_dcs') return 'dcs';
    return 'bearings';
  }

  if (dataZone === 'silver') {
    if (tableName.endsWith('_waveform_preprocessed')) return 'waveform_preprocessed';
    return 'config';
  }

  if (tableName.includes('diagnosis_result')) return 'diagnosis_result';
  return 'feature_preprocessed';
};

export const TABLE_LATEST_ROW_MAP = {
  // Bronze (12)
  kmp_ecms_bearings1: 4200,
  gsc_bearings: 1800,
  hanwha_impact_bearings1: 4600,
  gsp_ecms1_chart: 215000,
  kmp_ecms_trend: 36000,
  gsp_dcs: 98000,
  gsp_ecms1_bearings2: 5100,
  gsp_ecms1_bearings1: 5400,
  hanwha_impact_chart: 176000,
  gsp_ecms1_trend: 41000,
  hanwha_impact_trend: 28500,
  kmp_ecms_chart: 149000,

  // Silver (6)
  gsp_ecms1_waveform_preprocessed: 86000,
  hanwha_impact_config: 1900,
  kmp_ecms_config: 1700,
  hanwha_impact_waveform_preprocessed: 73000,
  gsp_ecms1_config: 2200,
  kmp_ecms_waveform_preprocessed: 64000,

  // Gold (6)
  gsp_ecms1_feature_preprocessed: 24500,
  kmp_ecms_feature_preprocessed: 21200,
  hanwha_impact_feature_preprocessed: 19800,
  kmp_ecms_diagnosis_results: 1400,
  gsp_ecms1_diagnosis_result: 1650,
  hanwha_impact_diagnosis_results: 1300,
} as const;

const getLatestTotalRows = (tableName: string): number => {
  const latestTotalRows = TABLE_LATEST_ROW_MAP[tableName as keyof typeof TABLE_LATEST_ROW_MAP];

  if (latestTotalRows === undefined) {
    console.warn(`[QualityDashboardIcebergData] Missing latest row map for table: ${tableName}`);
    return 0;
  }

  return latestTotalRows;
};

export const QUALITY_DASHBOARD_ICEBERG_TABLES: QualityDashboardIcebergTable[] = ALL_ICEBERG_DATA.map((hit, index) => {
  const source = hit._source as OpenMetadataSourceWithLineage;
  const serviceName = source.service?.name || '';
  const dataZone = getDataZone(serviceName);
  const tableName = source.name;
  const family = getFamily(tableName);
  const role = getRole(tableName, dataZone);
  const columnNames = source.columnNames ?? [];
  const upstreamLineageCount = source.upstreamLineage?.length ?? 0;

  return {
    tableId: index + 1,
    sourceId: hit._id,
    fqn: source.fullyQualifiedName,
    tableName,
    dataZone,
    dataZoneLabel: DATA_ZONE_LABEL_MAP[dataZone],
    service: serviceName,
    database: source.database?.name || dataZone,
    schema: source.databaseSchema?.name || 'g_turbo',
    namespace: source.databaseSchema?.fullyQualifiedName || `${serviceName}.${dataZone}.g_turbo`,
    columnNames,
    columnCount: columnNames.length,
    description: stripHtml(source.description),
    upstreamLineageCount,
    updatedAt: source.updatedAt,
    family,
    role,
    roleQualityRatio: QUALITY_DASHBOARD_ROLE_QUALITY_RATIO_MAP[role],
    latestTotalRows: getLatestTotalRows(tableName),
  };
});

if (QUALITY_DASHBOARD_ICEBERG_TABLES.length !== 24) {
  console.warn(
    `[QualityDashboardIcebergData] Expected 24 iceberg tables, received ${QUALITY_DASHBOARD_ICEBERG_TABLES.length}`
  );
}
